import { getBackendUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import storage from '../utils/storage';

const BACKEND_URL = getBackendUrl();
if (!BACKEND_URL) {
  console.error('Backend URL not configured. Please set REACT_APP_BACKEND_URL environment variable.');
}

const AdminAccessManagement = () => {
  const [activeTab, setActiveTab] = useState('grant');
  const [users, setUsers] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [packages, setPackages] = useState({});

  // Grant Free Access form
  const [quickEmail, setQuickEmail] = useState('');
  const [quickResult, setQuickResult] = useState(null);
  const [quickUsers, setQuickUsers] = useState([]);
  const [quickUsersLoading, setQuickUsersLoading] = useState(false);
  const [grantForm, setGrantForm] = useState({
    target_email: '',
    target_username: '',
    target_password: '',
    days: 30,
    reason: '',
    granted_features: [] // Selected features
  });

  // Extend Subscription form
  const [extendForm, setExtendForm] = useState({
    target_email: '',
    additional_days: 30,
    reason: ''
  });

  // Edit Modal state
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [editForm, setEditForm] = useState({
    days: 30,
    granted_features: []
  });

  // Delete Modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  // Fetch users and features on mount
  useEffect(() => {
    fetchUsers();
    fetchAccessLog();
    fetchAvailableFeatures();
    fetchQuickAccessUsers();
  }, []);

  const fetchAvailableFeatures = async () => {
    try {
      const token = storage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/available-features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableFeatures(response.data.features || {});
      setPackages(response.data.packages || {});
      // Default: select all features
      setGrantForm(prev => ({
        ...prev,
        granted_features: Object.keys(response.data.features || {})
      }));
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const fetchQuickAccessUsers = async () => {
    try {
      setQuickUsersLoading(true);
      const token = storage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/quick-access-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuickUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching quick access users:', error);
    } finally {
      setQuickUsersLoading(false);
    }
  };

  const handleDeleteQuickUser = async (username) => {
    if (!window.confirm(`Διαγραφή χρήστη ${username};`)) return;
    try {
      const token = storage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/quick-access-user/${encodeURIComponent(username)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `Ο χρήστης ${username} διαγράφηκε.` });
      fetchQuickAccessUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Σφάλμα κατά τη διαγραφή' });
    }
  };

  const handleResendQuickUser = async (username) => {
    try {
      const token = storage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/admin/quick-access-resend/${encodeURIComponent(username)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: response.data.message || `Email εστάλη ξανά στον ${username}` });
      fetchQuickAccessUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Σφάλμα κατά την αποστολή' });
    }
  };


  const fetchUsers = async () => {
    try {
      const token = storage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/users-with-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAccessLog = async () => {
    try {
      const token = storage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/access-log?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessLog(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching access log:', error);
    }
  };

  // Toggle a feature in the granted_features array
  const toggleFeature = (featureId) => {
    setGrantForm(prev => {
      const features = prev.granted_features || [];
      if (features.includes(featureId)) {
        return { ...prev, granted_features: features.filter(f => f !== featureId) };
      } else {
        return { ...prev, granted_features: [...features, featureId] };
      }
    });
  };

  // Select all features
  const selectAllFeatures = () => {
    setGrantForm(prev => ({
      ...prev,
      granted_features: Object.keys(availableFeatures)
    }));
  };

  // Deselect all features
  const deselectAllFeatures = () => {
    setGrantForm(prev => ({
      ...prev,
      granted_features: []
    }));
  };

  // Select features from a package
  const selectPackageFeatures = (packageId) => {
    const pkg = packages[packageId];
    if (pkg && pkg.features) {
      setGrantForm(prev => ({
        ...prev,
        granted_features: [...pkg.features]
      }));
    }
  };

  // Open Edit Modal
  const openEditModal = async (user) => {
    // Fetch user details to get current features
    try {
      const token = storage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/user-details/${encodeURIComponent(user.email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userData = response.data.user;
      const currentFeatures = userData.free_access?.granted_features || Object.keys(availableFeatures);
      
      setEditForm({
        days: user.days_remaining || 30,
        granted_features: currentFeatures
      });
      setEditModal({ open: true, user: user });
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Fallback: open modal with default values
      setEditForm({
        days: user.days_remaining || 30,
        granted_features: Object.keys(availableFeatures)
      });
      setEditModal({ open: true, user: user });
    }
  };

  // Open Delete Modal
  const openDeleteModal = (user) => {
    setDeleteModal({ open: true, user: user });
  };

  // Handle Update User
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = storage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/update-user-access`,
        {
          target_email: editModal.user.email,
          days: editForm.days,
          granted_features: editForm.granted_features
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: response.data.message });
      setEditModal({ open: false, user: null });
      fetchUsers();
      fetchAccessLog();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update user' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete User
  const handleDeleteUser = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = storage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/delete-user`,
        {
          target_email: deleteModal.user.email
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: response.data.message });
      setDeleteModal({ open: false, user: null });
      fetchUsers();
      fetchAccessLog();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete user' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = storage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/grant-free-access`,
        grantForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: `${response.data.message} | Username: ${response.data.username || 'N/A'}` });
      setGrantForm({ target_email: '', target_username: '', target_password: '', days: 30, reason: '', granted_features: Object.keys(availableFeatures) });
      fetchUsers();
      fetchAccessLog();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to grant access' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExtendSubscription = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = storage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/extend-subscription`,
        extendForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: response.data.message });
      setExtendForm({ target_email: '', additional_days: 30, reason: '' });
      fetchUsers();
      fetchAccessLog();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to extend subscription' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('el-GR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="admin-access-management p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
        🔑 Admin Access Management
      </h2>

      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-4 rounded ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('grant')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'grant'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          🎁 Grant Free Access
        </button>
        <button
          onClick={() => setActiveTab('extend')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'extend'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          ⏳ Extend Subscription
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'users'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          👥 Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('log')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'log'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          📊 Activity Log ({accessLog.length})
        </button>
        <button
          onClick={() => setActiveTab('quick')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'quick'
              ? 'text-green-600 border-b-2 border-green-600'
              : 'text-gray-600 hover:text-green-600'
          }`}
        >
          Γρήγορη Πρόσβαση 48ωρου
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'grant' && (
        <div className="grant-access-form">
          <h3 className="text-xl font-semibold mb-4">Δωρεάν Πρόσβαση σε Χρήστη</h3>
          <form onSubmit={handleGrantAccess} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Χρήστη *
              </label>
              <input
                type="email"
                value={grantForm.target_email}
                onChange={(e) => setGrantForm({ ...grantForm, target_email: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="user@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username *
              </label>
              <input
                type="text"
                value={grantForm.target_username}
                onChange={(e) => setGrantForm({ ...grantForm, target_username: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="π.χ. john, radioapp144"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Κωδικός Πρόσβασης (για νέους χρήστες)
              </label>
              <input
                type="text"
                value={grantForm.target_password}
                onChange={(e) => setGrantForm({ ...grantForm, target_password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="Αν είναι κενό, ο χρήστης θα κάνει login μόνο μέσω Google"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ημέρες: {grantForm.days}
              </label>
              <input
                type="range"
                min="1"
                max="365"
                value={grantForm.days}
                onChange={(e) => setGrantForm({ ...grantForm, days: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 ημέρα</span>
                <span>365 ημέρες (1 χρόνος)</span>
              </div>
            </div>

            {/* Feature Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Επιλογή Λειτουργιών ({grantForm.granted_features?.length || 0} επιλεγμένα)
              </label>
              
              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={selectAllFeatures}
                  className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 dark:bg-green-900 dark:text-green-300"
                >
                  ✓ Επιλογή Όλων
                </button>
                <button
                  type="button"
                  onClick={deselectAllFeatures}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200 dark:bg-red-900 dark:text-red-300"
                >
                  ✗ Αποεπιλογή Όλων
                </button>
                <span className="text-xs text-gray-400 self-center mx-2">|</span>
                {Object.entries(packages).map(([pkgId, pkg]) => (
                  <button
                    key={pkgId}
                    type="button"
                    onClick={() => selectPackageFeatures(pkgId)}
                    className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-300"
                  >
                    📦 {pkg.name_gr || pkg.name}
                  </button>
                ))}
              </div>

              {/* Feature Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700 max-h-60 overflow-y-auto">
                {Object.entries(availableFeatures).map(([featureId, feature]) => (
                  <label
                    key={featureId}
                    className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                      grantForm.granted_features?.includes(featureId)
                        ? 'bg-blue-100 dark:bg-blue-900'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={grantForm.granted_features?.includes(featureId) || false}
                      onChange={() => toggleFeature(featureId)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {feature.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Αιτιολογία (Προαιρετικό)
              </label>
              <textarea
                value={grantForm.reason}
                onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                rows="2"
                placeholder="Γιατί δίνετε δωρεάν πρόσβαση;"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !grantForm.granted_features?.length}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Επεξεργασία...' : '🎁 Δωρεάν Πρόσβαση'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'extend' && (
        <div className="extend-subscription-form">
          <h3 className="text-xl font-semibold mb-4">Extend User Subscription</h3>
          <form onSubmit={handleExtendSubscription} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                User Email *
              </label>
              <input
                type="email"
                value={extendForm.target_email}
                onChange={(e) => setExtendForm({ ...extendForm, target_email: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="user@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Days: {extendForm.additional_days}
              </label>
              <input
                type="range"
                min="1"
                max="365"
                value={extendForm.additional_days}
                onChange={(e) => setExtendForm({ ...extendForm, additional_days: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 day</span>
                <span>365 days</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={extendForm.reason}
                onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Why are you extending the subscription?"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400"
            >
              {loading ? 'Processing...' : '⏳ Extend Subscription'}
            </button>
          </form>
        </div>
      )}


      {activeTab === 'quick' && (
        <div className="quick-access-form">
          <h3 className="text-xl font-semibold mb-2">Γρήγορη Πρόσβαση 48ωρου</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Βάλε μόνο το email. Username &amp; password δημιουργούνται αυτόματα και αποστέλλονται στον χρήστη.
          </p>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!quickEmail.trim()) return;
            setLoading(true);
            setQuickResult(null);
            setMessage({ type: '', text: '' });
            try {
              const token = storage.getItem('token');
              const response = await axios.post(
                `${BACKEND_URL}/api/admin/quick-access`,
                { target_email: quickEmail.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setQuickResult(response.data);
              setMessage({ type: 'success', text: `Πρόσβαση δημιουργήθηκε! Username: ${response.data.username}` });
              setQuickEmail('');
              fetchQuickAccessUsers();
            } catch (err) {
              setMessage({ type: 'error', text: err.response?.data?.detail || 'Σφάλμα κατά τη δημιουργία' });
            } finally {
              setLoading(false);
            }
          }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Χρήστη *
              </label>
              <input
                type="email"
                value={quickEmail}
                onChange={(e) => setQuickEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600"
                placeholder="user@example.com"
                data-testid="quick-access-email"
              />
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span>Password: <strong>24free</strong></span>
              <span>Διάρκεια: <strong>48 ώρες</strong></span>
              <span>2FA: <strong>Απενεργοποιημένο</strong></span>
            </div>
            
            <button
              type="submit"
              disabled={loading || !quickEmail.trim()}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium transition-colors"
              data-testid="quick-access-submit"
            >
              {loading ? 'Δημιουργία...' : 'Αποστολή Πρόσβασης'}
            </button>
          </form>
          
          {quickResult && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg" data-testid="quick-access-result">
              <p className="font-semibold text-green-700 dark:text-green-400 mb-2">Πρόσβαση Δημιουργήθηκε!</p>
              <div className="space-y-1 text-sm">
                <p>Email: <strong>{quickResult.email}</strong></p>
                <p>Username: <strong>{quickResult.username}</strong></p>
                <p>Password: <strong>{quickResult.password}</strong></p>
                <p>Λήξη: <strong>{new Date(quickResult.expiration_date).toLocaleString('el-GR')}</strong></p>
                <p>Welcome Email: <strong>{quickResult.email_sent ? 'Στάλθηκε' : 'Αποτυχία αποστολής'}</strong></p>
              </div>
            </div>
          )}

          {/* Quick Access Users List */}
          <div className="mt-8">
            <h4 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">
              Quick Access Users ({quickUsers.length})
            </h4>
            {quickUsersLoading ? (
              <p className="text-gray-500">Φόρτωση...</p>
            ) : quickUsers.length === 0 ? (
              <p className="text-gray-500 text-sm">Δεν υπάρχουν quick-access users ακόμα.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Username</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Αποστολή</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Κατάσταση</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Λήξη</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Ενέργειες</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {quickUsers.map((u, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{u.email}</td>
                        <td className="px-3 py-2 font-mono text-gray-800 dark:text-gray-200">{u.username}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                          {u.created_at ? new Date(u.created_at).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="px-3 py-2">
                          {u.status === 'active' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              Συνδέθηκε
                            </span>
                          )}
                          {u.status === 'pending' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                              Δεν συνδέθηκε
                            </span>
                          )}
                          {u.status === 'expired' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                              {u.has_logged_in ? 'Έληξε' : 'Έληξε (δεν συνδέθηκε)'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">
                          {u.expiration ? new Date(u.expiration).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleResendQuickUser(u.username)}
                              className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                              title="Αποστολή email ξανά"
                              data-testid={`resend-quick-${u.username}`}
                            >
                              Resend
                            </button>
                            <button
                              onClick={() => handleDeleteQuickUser(u.username)}
                              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                              title="Διαγραφή χρήστη"
                              data-testid={`delete-quick-${u.username}`}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="users-list">
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Όλοι οι Χρήστες</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 shadow-sm">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Ημέρες</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Λήξη</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Πηγή</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Ενέργειες</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{user.email}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{user.username}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {user.is_active ? '✅ Active' : '❌ Expired'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {user.is_active ? `${user.days_remaining} ημ.` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">
                      {formatDate(user.subscription_expiration)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.subscription_source === 'admin_grant'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      }`}>
                        {user.subscription_source || 'stripe'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        {user.role !== 'admin' && (
                          <>
                            <button
                              onClick={() => openEditModal(user)}
                              className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                              title="Επεξεργασία"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => openDeleteModal(user)}
                              className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                              title="Διαγραφή"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        )}
                        {user.role === 'admin' && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">Admin</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4 dark:text-white">
              ✏️ Επεξεργασία: {editModal.user?.username}
            </h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={editModal.user?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Νέα Διάρκεια: {editForm.days} ημέρες
                </label>
                <input
                  type="range"
                  min="1"
                  max="365"
                  value={editForm.days}
                  onChange={(e) => setEditForm({ ...editForm, days: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 ημέρα</span>
                  <span>365 ημέρες</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Λειτουργίες ({editForm.granted_features?.length || 0} επιλεγμένα)
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, granted_features: Object.keys(availableFeatures) })}
                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    ✓ Όλα
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, granted_features: [] })}
                    className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    ✗ Κανένα
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 max-h-40 overflow-y-auto">
                  {Object.entries(availableFeatures).map(([featureId, feature]) => (
                    <label
                      key={featureId}
                      className={`flex items-center p-2 rounded cursor-pointer ${
                        editForm.granted_features?.includes(featureId)
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={editForm.granted_features?.includes(featureId) || false}
                        onChange={() => {
                          const features = editForm.granted_features || [];
                          if (features.includes(featureId)) {
                            setEditForm({ ...editForm, granted_features: features.filter(f => f !== featureId) });
                          } else {
                            setEditForm({ ...editForm, granted_features: [...features, featureId] });
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{feature.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Αποθήκευση...' : '💾 Αποθήκευση'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditModal({ open: false, user: null })}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-lg font-semibold hover:bg-gray-400"
                >
                  Ακύρωση
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4 text-red-600">
              🗑️ Διαγραφή Χρήστη
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη <strong>{deleteModal.user?.username}</strong> ({deleteModal.user?.email})?
            </p>
            <p className="text-red-500 text-sm mb-4">
              ⚠️ Αυτή η ενέργεια δεν μπορεί να αναιρεθεί!
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteUser}
                disabled={loading}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400"
              >
                {loading ? 'Διαγραφή...' : '🗑️ Διαγραφή'}
              </button>
              <button
                onClick={() => setDeleteModal({ open: false, user: null })}
                className="flex-1 bg-gray-300 text-gray-800 py-2 rounded-lg font-semibold hover:bg-gray-400"
              >
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'log' && (
        <div className="activity-log">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Admin Activity Log</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300 shadow-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Target User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Days</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {accessLog.map((log, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.admin_username}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        log.action === 'grant_free_access'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {log.action === 'grant_free_access' ? '🎁 Grant' : '⏳ Extend'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900 font-medium">{log.target_username}</div>
                      <div className="text-xs text-gray-600">{log.target_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">
                      {log.days_granted || log.days_added} days
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      {log.reason || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccessManagement;
